import { useEffect } from "react";
import { useNavigate } from "react-router";
import userflow from "userflow.js";

import setLocation from "shared/setLocation";

// Userflow content authors the URL, so anything that could execute as script (javascript:) or
// carry an inline payload (data:, blob:) must never reach the browser.
const NAVIGABLE_PROTOCOLS = ["http:", "https:"];

/**
 * Routes Userflow's own navigation (step buttons, checklist task links) through the router, so
 * content that sends users to another page updates the app instead of reloading it.
 */
const useUserflowNavigate = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Any call on the userflow.js stub loads its script, which must not happen where Userflow
    // isn't configured at all — self-hosted instances above all.
    if (!process.env.REACT_APP_USERFLOW_TOKEN) {
      return;
    }

    userflow.setCustomNavigate((url) => {
      let target: URL;

      try {
        target = new URL(url, window.location.href);
      } catch {
        return;
      }

      if (!NAVIGABLE_PROTOCOLS.includes(target.protocol)) {
        return;
      }

      if (target.origin !== window.location.origin) {
        setLocation(url);
        return;
      }

      // The router can't push a path starting with "//" — the browser reads it as an origin
      const pathname = target.pathname.replace(/\/{2,}/g, "/");

      navigate(`${pathname}${target.search}${target.hash}`);
    });

    return () => {
      userflow.setCustomNavigate(null);
    };
  }, [navigate]);
};

export default useUserflowNavigate;
